/**
 * 小助理 ·「网友评价」面板（批 C 功能先行版，设计=docs/新增功能-年度奖项与网友评价.md §5）。
 *
 * 评论条 2026-10-23 茄子令「与媒体弹窗评论区对齐」＝直接抽公共组件 CommentFloor.ts
 * （圆头像 + 昵称 + 右浮点赞 + 文本 + 分隔虚线，三处共用一套样式）；标题行保留本页自己的。
 * 抽取全部走 ReviewSystem.pickReviews()（5 条、同月不重、占位符替换），面板自身⛔ 无状态。
 */
import { reviewSystem } from '../../systems/ReviewSystem';
import { GameState } from '../../systems/GameState';
import { Utils } from '../../core/Utils';
import { createDiv, createModalDismiss } from '../ui';
import { avatarColor, createCommentFloor } from '../CommentFloor';
import { buzzOfFame, commentCount, floorLikeCount, mediaSeed } from '../buzzReads';

/**
 * 点赞数＝**数值事件同款口径**（2026-10-23 茄子令）：锚＝按名气换算的话题度
 * （`buzzOfFame`，名气越高越高、封顶 300，同 plotStage 数值随机事件那把尺），
 * 再走媒体评论区同一套派生：讨论数＝锚×带、楼层赞＝首楼最高逐层衰减（同批天然降序）。
 * 仍是现场生成的展示读数：不落盘、不进结算，同月同名气同文案 ⇒ 同数（做种不跳）。
 */
function likeCountOf(text: string, floor: number): string {
    const gs = GameState.getInstance();
    const seed = mediaSeed(text);
    const comments = commentCount(buzzOfFame(gs.getFame()), seed);
    return Utils.formatCountCN(floorLikeCount(comments, seed, floor));
}

export class ReviewPanel {
    readonly element: HTMLElement;
    private readonly listBox: HTMLElement;
    /** 弹窗开合时通知宿主（悬浮入口要让位，与 VN 纸卡同一条规矩） */
    onOpenChanged?: (open: boolean) => void;

    constructor() {
        const wrap = createDiv('modal-wrap');
        const card = createDiv('modal-note-card review-card');

        const head = createDiv('review-head');
        head.appendChild(createDiv('review-title', ['网友评价']));
        card.appendChild(head);

        this.listBox = createDiv('review-list');
        card.appendChild(this.listBox);

        // 2026-10-16 弹窗统一批（茄子令）：卡内那颗「关闭」删掉，关闭热区＝遮罩本体（见 ui.ts createModalDismiss）。
        // 2026-09-30 那条「关闭别用白色 cancel 样式、与其它按钮一致」随这颗钮一起作废，别照旧样式铺回来。
        // 标题行是本页自己的 .review-title（不是通用 .mtitle），modal.ts 取不到名字，单独声明一份
        wrap.dataset.dialogLabel = '网友评价';
        wrap.appendChild(createModalDismiss(() => this.close()));
        wrap.appendChild(card);
        this.element = wrap;
    }

    private isOpen = false;

    open(): void {
        if (this.isOpen) return;
        this.isOpen = true;
        this.render();
        this.element.classList.add('show');
        this.onOpenChanged?.(true);
    }

    close(): void {
        if (!this.isOpen) return;
        this.isOpen = false;
        this.element.classList.remove('show');
        this.onOpenChanged?.(false);
    }

    private render(): void {
        const reviews = reviewSystem.pickReviews();
        this.listBox.replaceChildren();
        if (reviews.length === 0) {
            this.listBox.appendChild(createDiv('review-empty', ['还没有人讨论你——先让作品说话吧。']));
            return;
        }
        let floor = 0;
        for (const r of reviews) {
            this.listBox.appendChild(createCommentFloor({
                name: r.speaker,
                text: r.text,
                avatarBg: avatarColor(r.text),
                likes: likeCountOf(r.text, floor++),
            }));
        }
    }
}
